/**
 * 路線図 View で、最後に見えた停留所から線の上を進める。
 *
 * **置ける事実は「その時刻にその停留所にいた」だけ。**そこから先は時刻表の
 * 所要で進めた推定で、遅れが読めていない便ほど外れる（遅れは BusWatch の
 * 観測からは読めない。OPERATION_ANALYSIS_PLAN.md §10）。
 *
 * ## 節を 1 つずつ歩いてはいけない
 *
 * **便は節を飛ばし、線の途中で終わる。**その便が実際に停まる並び（path）に
 * 沿って進める。
 *
 *     天神行き      11 → 10 → 9 → 8 → 6      （7 を飛ばし、6 で終わる）
 *     大濠公園行き  11 → 10 → 9 → 7 → 6 … 0  （8 を飛ばす）
 *
 * 全部の節を歩くと、**通らない停留所で停まり**（大濠公園行きが天神四丁目に、
 * 天神行きが天神中央郵便局前に停まった）、**終わりを知らずに線の端まで進む**
 * （天神行きが天神三丁目で消えずに大濠公園へ走り続けた）。どちらも実際に
 * 出た不具合。
 *
 * ## 進め方（区間の所要を M 秒、停まっている時間を D 秒とすると）
 *
 *     経過 0 〜 D   その停留所にいる    moving=false
 *     経過 D 〜 M   区間を滑る          moving=true
 *     経過 M で次の停留所へ送り、また同じ規則を繰り返す
 *
 * **D は区間の半分か 30 秒の、短いほう。**半分ずつに割ると、10 分かかる
 * 都市高速の区間で**5 分も停留所に留まる**ことになる（実際は定刻で出て、
 * ほぼ全部を高速の上で過ごす）。1 分の区間では 30 秒ずつで変わらない。
 *
 * 呼び出し側は **moving で見た目を分ける**（塗り＝停留所、輪郭＝推定）。
 * 動いて見えることと、位置が分かっていることは別なので。
 *
 * ## 速さも返す
 *
 * `rate`（1 秒あたりに進む節）は積み分けが使う。**速さが違う 2 台は
 * 放っておけば離れる**（追い抜き）ので、重なっても直さない。同じ速さの
 * 2 台は**ずっと重なったまま**なのでずらす（utils/lineMapStack.js）。
 * 停まっているあいだと、進めないあいだは 0。
 *
 * ## 止まり方は 2 通りある。**混ぜない**
 *
 *     atEdge=true   その便の並びの終わりまで来た。**この先は走っていない
 *                   か、線の外にいる。**呼び出し側は heldSec を見て消す
 *     atEdge=false  次の区間の所要が分からない。**進めずに待つ**
 *
 * 所要が分からないほうは消さない。分からないことと、そこに居ないことは別。
 *
 * ## 節を持たない図もある
 *
 * 手で描いた路線図（`LineDiagramView`）は位置を (行, 列) で持っていて、
 * **節番号が無い。**進め方は同じ（時刻表の定刻で区間を割る）なので、ここは
 * そのまま使い、位置は `index`（何番目の区間か）と `ratio`（その区間の
 * どこまで来たか）から画面が出す。節番号の `at` と `rate` はその図では
 * 0 のままで、**誰も読まない。**
 */

/**
 * @param {Array<{node?: number, minutes: number}>} path その便が停まる並び
 * @param {number} startIndex path の何番目にいるか
 * @param {number} elapsedSec その停留所で見えてからの経過（秒）
 * @returns {{at: number, moving: boolean, atEdge: boolean, heldSec: number,
 *            rate: number, ratio: number,
 *            index: number, from: number, to: number}}
 *   at は小数の節番号（描画位置）、heldSec は終わりに着いてからの経過（秒）、
 *   index は path の何番目の段にいるか（**都市高速の判定に使う** —
 *   その段の `express` を読む）、ratio はその区間のどこまで来たか（0〜1。
 *   **節を持たない図はこちらで位置を出す**）、from/to はいまいる区間の
 *   両端の節番号（停留所にいるときは同じ値）
 */
export function advance(path, startIndex, elapsedSec) {
  if (! Array.isArray(path) || path.length === 0) {
    return {
      at: 0, moving: false, atEdge: false, heldSec: 0, rate: 0, ratio: 0,
      index: 0, from: 0, to: 0,
    };
  }

  let i = Math.max(0, Math.min(startIndex ?? 0, path.length - 1));
  let t = Math.max(0, elapsedSec);

  for (let guard = 0; guard < 200; guard++) {
    const here = path[i];
    const next = path[i + 1];

    // 並びの終わり。**この便はもう線の上を走っていない**
    if (! next) {
      return {
        at: here.node, moving: false, atEdge: true, heldSec: t, rate: 0, ratio: 0,
        index: i, from: here.node, to: here.node,
      };
    }

    const total = (next.minutes - here.minutes) * 60;
    // 定刻が進んでいない（同分・逆行）。**当てずに待つ**
    if (! (total > 0)) {
      return {
        at: here.node, moving: false, atEdge: false, heldSec: 0, rate: 0, ratio: 0,
        index: i, from: here.node, to: here.node,
      };
    }

    // **停まっている時間は 30 秒まで。**区間が長いほど、半分ずつに割る
    // のは実際と合わなくなる（都市高速の 10 分で 5 分も停まらない）
    const dwell = Math.min(total / 2, DWELL_SEC);

    if (t < dwell) {
      return {
        at: here.node, moving: false, atEdge: false, heldSec: 0, rate: 0, ratio: 0,
        index: i, from: here.node, to: here.node,
      };
    }
    if (t < total) {
      // 節を飛ばす区間もあるので、節番号のあいだを補間する。
      // **残りの時間で割る** — 停まっている時間を引いたぶんが走る時間
      const ratio = (t - dwell) / (total - dwell);

      return {
        at: (here.node ?? 0) + ((next.node ?? 0) - (here.node ?? 0)) * ratio,
        moving: true, atEdge: false, heldSec: 0, ratio,
        index: i, from: here.node, to: next.node,
        // **1 秒あたりに進む節。**積み分けが使う — 速さが違う 2 台は
        // 放っておけば離れる（追い抜き）ので重なりを直さない
        rate: ((next.node ?? 0) - (here.node ?? 0)) / (total - dwell),
      };
    }

    t -= total;
    i += 1;
  }

  return {
    at: path[i].node, moving: false, atEdge: false, heldSec: 0, rate: 0, ratio: 0,
    index: i, from: path[i].node, to: path[i].node,
  };
}

/**
 * 停留所に停まっている時間（秒）。**区間の半分を超えない。**
 *
 * 「バス停間が 1 分であれば、0〜30 秒はそのバス停、31〜59 秒はバス停の
 * あいだで」という見え方をそのまま保ちつつ、長い区間でも停まりすぎない
 * ようにするための上限。
 */
export const DWELL_SEC = 30;

/** 終わりに着いてから、この秒数はそのまま出す */
export const HOLD_SEC = 60;

/** そのあと、この秒数かけて消す */
export const FADE_SEC = 5;

/**
 * 終わりに着いた便の見え方。1 が出しきり、0 が消えきり。
 *
 * **着いた瞬間に消さない。**着いたことが見えないまま消えると、取りこぼした
 * のか着いたのか分からなくなる。1 分そのまま出してから薄くする。
 *
 * **図の外へ走り去る便（`leaves`）は、ふつうの停車ぶんだけ停まって消す。**
 * どこにも着いていないので、1 分も残すと停まっているように見える
 * （26 福間駅前行きは特別支援学校前から先を描いていない）。
 */
export function edgeOpacity(atEdge, heldSec, leaves = false) {
  if (! atEdge) return 1;
  const hold = leaves ? DWELL_SEC : HOLD_SEC;
  if (heldSec <= hold) return 1;
  if (heldSec >= hold + FADE_SEC) return 0;

  return 1 - (heldSec - hold) / FADE_SEC;
}
